import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import '../dashboard/dashboard.css';

interface AdminSite {
  id: string;
  title: string;
  slug: string | null;
  state: 'draft' | 'published' | 'suspended';
  ownerEmail: string;
  templateId: string;
  createdAt: string;
  publishedAt: string | null;
}

export function AdminSitesPage() {
  const [sites, setSites] = useState<AdminSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const state = searchParams.get('state') ?? '';

  function loadSites() {
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    if (state) query.set('state', state);
    api
      .get<{ sites: AdminSite[] }>(`/admin/sites${query.toString() ? `?${query.toString()}` : ''}`)
      .then((data: { sites: AdminSite[] }) => setSites(data.sites))
      .catch((err: Error) => setError(err.message || 'Could not load sites.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, state]);

  function updateSearch(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('search', value);
    else next.delete('search');
    setSearchParams(next, { replace: true });
  }

  function updateState(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('state', value);
    else next.delete('state');
    setSearchParams(next, { replace: true });
  }

  async function toggleSuspend(siteId: string, suspend: boolean) {
    setError(null);
    try {
      await api.post(`/admin/sites/${siteId}/${suspend ? 'suspend' : 'unsuspend'}`, {});
      loadSites();
    } catch (err) {
      setError((err as Error).message || 'Could not update site.');
    }
  }

  return (
    <div className="admin-sites">
      <h1>Sites</h1>
      <div className="filters">
        <input
          type="search"
          placeholder="Search by title, slug or owner email"
          value={search}
          onChange={(e) => updateSearch(e.target.value)}
        />
        <select value={state} onChange={(e) => updateState(e.target.value)}>
          <option value="">All states</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {loading && <p className="dash-page-subtitle">Loading sites…</p>}
      {error && (
        <div className="sarnia-card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Owner</th>
              <th>Template</th>
              <th>State</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((site) => (
              <tr key={site.id}>
                <td>{site.title}</td>
                <td>{site.slug ?? '—'}</td>
                <td>{site.ownerEmail}</td>
                <td>{site.templateId}</td>
                <td>
                  <span
                    className={`sarnia-badge ${
                      site.state === 'published'
                        ? 'sarnia-badge--success'
                        : site.state === 'suspended'
                          ? 'sarnia-badge--danger'
                          : 'sarnia-badge--neutral'
                    }`}
                  >
                    {site.state}
                  </span>
                </td>
                <td>{new Date(site.createdAt).toLocaleDateString()}</td>
                <td>
                  {site.state === 'suspended' ? (
                    <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => toggleSuspend(site.id, false)}>
                      Unsuspend
                    </button>
                  ) : (
                    <button type="button" className="sarnia-btn sarnia-btn--secondary sarnia-btn--sm" onClick={() => toggleSuspend(site.id, true)}>
                      Suspend
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && sites.length === 0 && <p className="dash-page-subtitle">No sites match.</p>}
    </div>
  );
}
